// src/pages/PricingPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSubscription } from '../hooks/useSubscription';
import PlanGrid from '../components/subscription/PlanGrid';
import CheckoutModal from '../components/subscription/CheckoutModal';
import { loadStripe } from '@stripe/stripe-js';
import { FaCheck, FaVideo, FaBoxOpen, FaArrowRight } from 'react-icons/fa';

/* ─── small reusable ──────────────────────────────────────────────────────── */
const FeaturePill = ({ children }) => (
  <div className="flex items-center gap-2 text-sm text-gray-700">
    <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center shrink-0">
      <FaCheck className="text-[9px]"/>
    </span>
    {children}
  </div>
);

/* ─── page ────────────────────────────────────────────────────────────────── */
const PricingPage = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user }  = useSelector(s => s.auth);
  const { plans, loading, error, loadPlans } = useSubscription();

  const [selectedPlan,  setSelectedPlan]  = useState(null);
  const [showCheckout,  setShowCheckout]  = useState(false);
  const [stripe,        setStripe]        = useState(null);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [activeTab,     setActiveTab]     = useState('payg'); // 'payg' | 'sub'

  useEffect(() => { loadPlans(); }, [loadPlans]);

  useEffect(() => {
    const init = async () => {
      try {
        const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
        if (key) setStripe(await loadStripe(key));
      } finally { setStripeLoading(false); }
    };
    init();
  }, []);

  const handlePlanSelect = plan => {
    if (!user) { navigate('/login', { state: { from: location.pathname || '/pricing' } }); return; }
    setSelectedPlan(plan);
    setShowCheckout(true);
  };

  const handleCheckoutSuccess = result => {
    setShowCheckout(false); setSelectedPlan(null);
    navigate('/account/subscription', {
      state: { message: result.type === 'free' ? 'Free subscription started!' : 'Payment successful! Subscription is now active.' },
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ══ HERO ════════════════════════════════════════════════════════════ */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          {/* badge */}
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-red/8 text-brand-red text-xs font-bold uppercase tracking-widest mb-5">
            Simple Pricing
          </span>
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight tracking-tight mb-4">
            Pay for exactly what you need
          </h1>
          <p className="text-gray-500 text-lg leading-relaxed max-w-xl mx-auto">
            No hidden fees. No long-term commitments.
            Pay per video whenever you want, or add a subscription for storage and monthly credits.
          </p>

          {!user && (
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/register"
                className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-brand-red text-white font-bold rounded-xl hover:bg-red-700 transition-colors shadow-sm">
                Get Started Free <FaArrowRight className="text-sm"/>
              </Link>
              <Link to="/login"
                className="inline-flex items-center justify-center px-7 py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors">
                Sign In
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ══ TABS ════════════════════════════════════════════════════════════ */}
      <div className="sticky top-16 z-30 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-screen-md mx-auto px-6 flex">
          {[
            { key: 'payg', Icon: FaVideo,   label: 'Pay-Per-Video',  sub: 'No subscription' },
            { key: 'sub',  Icon: FaBoxOpen, label: 'Subscriptions',  sub: 'Monthly plans' },
          ].map(({ key, Icon, label, sub }) => (
            <button key={key} onClick={() => setActiveTab(key)}
              className={['flex-1 flex items-center gap-3 px-4 py-4 text-left border-b-2 transition-all duration-150',
                activeTab === key ? 'border-brand-red text-brand-red' : 'border-transparent text-gray-500 hover:text-gray-800',
              ].join(' ')}>
              <Icon className={`text-lg shrink-0 ${activeTab === key ? 'text-brand-red' : 'text-gray-400'}`}/>
              <div>
                <p className="text-sm font-bold leading-none">{label}</p>
                <p className="text-[11px] mt-0.5 opacity-70">{sub}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* ══ CONTENT ═════════════════════════════════════════════════════════ */}
      <main className="max-w-screen-lg mx-auto px-6 py-12">

        {/* ── Pay-Per-Video tab ── */}
        {activeTab === 'payg' && (
          <div style={{animation:'fadeUp .2s ease-out'}}>
            {/* Context block */}
            <div className="text-center mb-10">
              <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Pay only when you need a video</h2>
              <p className="text-gray-500 max-w-md mx-auto text-sm">No subscription required. Works on the free plan. Just buy the specific video you want processed.</p>
            </div>

            {/* Pricing cards */}
            <div className="grid sm:grid-cols-3 gap-5 max-w-2xl mx-auto mb-12">
              {[
                { range: '≤ 30 min', price: '$3.99', accent: false },
                { range: '30 – 60 min', price: '$5.99', accent: true },
                { range: '60 – 120 min', price: '$9.99', accent: false },
              ].map(({ range, price, accent }) => (
                <div key={range} className={['rounded-2xl p-6 text-center border transition-all duration-200',
                  accent
                    ? 'bg-brand-red text-white border-brand-red shadow-lg shadow-brand-red/20 scale-[1.03]'
                    : 'bg-white border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5',
                ].join(' ')}>
                  <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${accent ? 'text-white/70' : 'text-gray-400'}`}>
                    {range}
                  </p>
                  <p className={`text-4xl font-black mb-1 ${accent ? 'text-white' : 'text-gray-900'}`}>{price}</p>
                  <p className={`text-xs ${accent ? 'text-white/60' : 'text-gray-400'}`}>per video</p>
                  {accent && <span className="inline-block mt-3 px-3 py-0.5 bg-white/20 rounded-full text-[10px] font-bold text-white/90">Most common</span>}
                </div>
              ))}
            </div>

            {/* What you get */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-7 max-w-2xl mx-auto">
              <h3 className="font-bold text-gray-900 mb-5 text-sm">What's included</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  'Pay only when needed — no monthly commitment',
                  'Works on the free plan, no upgrade required',
                  'Video saved to your personal storage',
                  'Download in HD and share instantly',
                  'Accessible from your dashboard anytime',
                  'Same-day processing guarantee',
                ].map(f => <FeaturePill key={f}>{f}</FeaturePill>)}
              </div>
            </div>
          </div>
        )}

        {/* ── Subscriptions tab ── */}
        {activeTab === 'sub' && (
          <div style={{animation:'fadeUp .2s ease-out'}}>
            <div className="text-center mb-10">
              <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Add a subscription for more power</h2>
              <p className="text-gray-500 max-w-md mx-auto text-sm">
                Ideal for teams and regulars — more storage, free monthly video credits, and priority processing.
              </p>
            </div>

            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden p-6 lg:p-8">
              <PlanGrid
                plans={plans}
                onPlanSelect={handlePlanSelect}
                loading={loading}
                error={error}
              />
            </div>

            {/* Free tier note */}
            <p className="text-center text-xs text-gray-400 mt-6">
              All plans include a free tier. No credit card required to get started.{' '}
              {!user && (
                <Link to="/register" className="text-brand-red font-semibold hover:underline">Create your free account →</Link>
              )}
            </p>
          </div>
        )}
      </main>

      {/* ══ FAQ / COMPARE STRIP ══════════════════════════════════════════════ */}
      <section className="bg-white border-t border-gray-100 py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl font-extrabold text-gray-900 text-center mb-10">Frequently asked questions</h2>
          <div className="space-y-6">
            {[
              { q: 'Do I need a subscription to purchase a video?', a: 'No. You can purchase any video with pay-per-video pricing, no subscription required. Create a free account and buy only what you want.' },
              { q: 'What happens to my videos if I cancel my subscription?', a: 'Your videos stay in your account. You simply lose access to subscription perks like free monthly credits and extra storage — your purchased content is always yours.' },
              { q: 'How quickly will my video be processed?', a: 'Most videos are ready within minutes of purchase. During peak times it may take up to 30 minutes. You\'ll receive a notification as soon as your video is ready.' },
              { q: 'Can I share my videos on social media?', a: 'Absolutely. Download your video in HD directly from your dashboard and share on any platform — Instagram, TikTok, YouTube, wherever you want.' },
            ].map(({ q, a }) => (
              <div key={q} className="pb-6 border-b border-gray-100 last:border-0">
                <p className="font-semibold text-gray-900 text-sm mb-2">{q}</p>
                <p className="text-sm text-gray-500 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ BOTTOM CTA ══════════════════════════════════════════════════════ */}
      {!user && (
        <section className="bg-gray-900 py-16 px-6 text-center">
          <h2 className="text-3xl font-black text-white mb-3">Ready to capture your moments?</h2>
          <p className="text-gray-400 mb-8 max-w-sm mx-auto text-sm">Create a free account and buy your first video in under 2 minutes.</p>
          <Link to="/register"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-brand-red text-white font-bold rounded-xl hover:bg-red-700 transition-colors shadow-lg shadow-brand-red/30">
            Get Started Free <FaArrowRight/>
          </Link>
        </section>
      )}

      {/* ══ CHECKOUT ════════════════════════════════════════════════════════ */}
      {selectedPlan && (
        <CheckoutModal
          isOpen={showCheckout}
          onClose={() => { setShowCheckout(false); setSelectedPlan(null); }}
          plan={selectedPlan}
          stripe={stripe}
          onSuccess={handleCheckoutSuccess}
          onError={() => {}}
          actionType="new"
        />
      )}

      {/* Stripe init overlay */}
      {stripeLoading && (
        <div className="fixed inset-0 z-50 bg-white/70 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-8 py-6 text-center">
            <div className="w-8 h-8 border-[3px] border-brand-red border-t-transparent rounded-full animate-spin mx-auto mb-3"/>
            <p className="text-sm text-gray-600">Initializing payment system…</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPage;
